<script setup>
  import { ref, onMounted, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import OverviewCards from './OverviewCards.vue';
  import TimeSeriesChart from './TimeSeriesChart.vue';
  import ComparisonTable from './ComparisonTable.vue';
  import CategoryBreakdown from './CategoryBreakdown.vue';
  import Loader from '../ui/Loader.vue';

  const { t } = useI18n();
  const i18nScope = 'components.sales_analytics';

  const props = defineProps({
    libraryId: {
      type: Number,
      required: true
    },
    libraryName: {
      type: String,
      required: true
    }
  });

  const loading = ref(true);
  const error = ref(null);
  const dashboardData = ref(null);
  const startDate = ref(getDefaultStartDate());
  const endDate = ref(getDefaultEndDate());
  const selectedComparisonType = ref('territory');

  function getDefaultStartDate() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  const availableComparisonTypes = computed(() => {
    if (!dashboardData.value?.comparisons) return [];
    return Object.keys(dashboardData.value.comparisons);
  });

  const currentComparison = computed(() => {
    if (!dashboardData.value?.comparisons) return null;
    return dashboardData.value.comparisons[selectedComparisonType.value];
  });

  async function fetchDashboardData() {
    loading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams({
        start_date: startDate.value,
        end_date: endDate.value
      });

      const response = await fetch(`/library/sales_analytics/dashboard?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      dashboardData.value = await response.json();

      // Set default comparison type to first available
      if (availableComparisonTypes.value.length > 0 && !availableComparisonTypes.value.includes(selectedComparisonType.value)) {
        selectedComparisonType.value = availableComparisonTypes.value[0];
      }
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
      error.value = t(`${i18nScope}.error_loading`);
    } finally {
      loading.value = false;
    }
  }

  function handleDateChange() {
    fetchDashboardData();
  }

  function formatComparisonTypeLabel(type) {
    return t(`${i18nScope}.comparison_types.${type}`);
  }

  onMounted(() => {
    fetchDashboardData();
  });
</script>

<template>
  <div class="sales-analytics-dashboard">
    <!-- Header with date filters -->
    <div class="dashboard-header mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-auto">
          <label class="form-label">{{ t(`${i18nScope}.start_date`) }}</label>
          <input
            type="date"
            class="form-control"
            v-model="startDate"
            @change="handleDateChange"
          />
        </div>
        <div class="col-auto">
          <label class="form-label">{{ t(`${i18nScope}.end_date`) }}</label>
          <input
            type="date"
            class="form-control"
            v-model="endDate"
            @change="handleDateChange"
          />
        </div>
        <div class="col-auto">
          <button class="btn btn-primary" @click="fetchDashboardData">
            {{ t(`${i18nScope}.refresh`) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center py-5">
      <Loader />
      <p class="mt-3">{{ t(`${i18nScope}.loading`) }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Dashboard content -->
    <div v-else-if="dashboardData">
      <!-- Overview Cards -->
      <OverviewCards
        :overview="dashboardData.overview"
        :comparison="currentComparison"
        :library-name="libraryName"
      />

      <!-- Time Series Chart -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">{{ t(`${i18nScope}.purchases_over_time`) }}</h5>
        </div>
        <div class="card-body">
          <TimeSeriesChart
            :library-data="dashboardData.overview.by_month"
            :peer-data="currentComparison?.by_month"
            :library-name="libraryName"
            :peer-label="currentComparison?.comparison_label"
          />
        </div>
      </div>

      <!-- Comparison Section -->
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ t(`${i18nScope}.peer_comparison`) }}</h5>
          <div class="btn-group" role="group">
            <button
              v-for="type in availableComparisonTypes"
              :key="type"
              type="button"
              class="btn btn-sm"
              :class="selectedComparisonType === type ? 'btn-primary' : 'btn-outline-primary'"
              @click="selectedComparisonType = type"
            >
              {{ formatComparisonTypeLabel(type) }}
            </button>
          </div>
        </div>
        <div class="card-body">
          <ComparisonTable
            v-if="currentComparison?.sufficient_data"
            :overview="dashboardData.overview"
            :comparison="currentComparison"
            :library-name="libraryName"
          />
          <div v-else class="alert alert-info mb-0" role="status">
            {{ t(`${i18nScope}.insufficient_peer_data`) }}
          </div>
        </div>
      </div>

      <!-- Category Breakdown -->
      <div class="row">
        <div class="col-md-6">
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">{{ t(`${i18nScope}.by_category`) }}</h5>
            </div>
            <div class="card-body">
              <CategoryBreakdown
                :data="dashboardData.overview.by_category"
                :comparison-data="currentComparison?.by_category"
                :show-comparison="currentComparison?.sufficient_data"
              />
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">{{ t(`${i18nScope}.by_format`) }}</h5>
            </div>
            <div class="card-body">
              <CategoryBreakdown
                :data="dashboardData.overview.by_format"
                :comparison-data="currentComparison?.by_format"
                :show-comparison="false"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Top Imprints -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">{{ t(`${i18nScope}.top_publishers`) }}</h5>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ t(`${i18nScope}.publisher`) }}</th>
                  <th class="text-end">{{ t(`${i18nScope}.purchases`) }}</th>
                  <th class="text-end">{{ t(`${i18nScope}.loans`) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="imprint in dashboardData.overview.by_imprint?.slice(0, 10)" :key="imprint.key">
                  <td>{{ imprint.name }}</td>
                  <td class="text-end">{{ imprint.count }}</td>
                  <td class="text-end">{{ imprint.loans }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top Items -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">{{ t(`${i18nScope}.top_items`) }}</h5>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{{ t(`${i18nScope}.item`) }}</th>
                  <th>{{ t(`${i18nScope}.author`) }}</th>
                  <th class="text-end">{{ t(`${i18nScope}.purchases`) }}</th>
                  <th class="text-end">{{ t(`${i18nScope}.loans`) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in dashboardData.overview.top_items?.slice(0, 10)" :key="item.item_id">
                  <td>{{ item.title }}</td>
                  <td>{{ item.author }}</td>
                  <td class="text-end">{{ item.count }}</td>
                  <td class="text-end">{{ item.loans }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .sales-analytics-dashboard {
    padding: 20px 0;
  }
</style>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
